import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import SEO from '../components/SEO';
import EmptyState from '../components/EmptyState';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

import {
  Archive,
  ArrowCounterClockwise,
  ArrowRight,
  Buildings,
  Calendar,
  CircleNotch,
  Clock,
  DotsThreeVertical,
  Eye,
  Lock,
  MagnifyingGlass,
  Plus,
  Scan,
  TrendUp,
  Trophy
} from '@phosphor-icons/react';
const MyAssessments = () => {
  const navigate = useNavigate();
  const { plan, planLimits } = usePlan();
  const { activeOrganization, loading: orgLoading } = useOrganization();
  const [assessments, setAssessments] = useState([]);
  const [showMenuFor, setShowMenuFor] = useState(null);
  const [menuFlipUp, setMenuFlipUp] = useState(false); // Track if menu should flip upward
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [assessmentLimit, setAssessmentLimit] = useState(null);
  const menuRef = useRef(null);
  const [filters, setFilters] = useState({
    search: '',
    grade: 'all',
    status: 'active' // 'active' or 'archived'
  });

  useEffect(() => {
    if (orgLoading) return;
    
    if (!activeOrganization) {
      // No org selected, stop loading and show empty state
      setLoading(false);
      return;
    }
    
    // Reset state when organization changes to prevent showing stale data
    setAssessments([]);
    setLoading(true);
    
    fetchAssessments();
    fetchAssessmentLimit();
  }, [activeOrganization, orgLoading, filters.status]);

  // Fetch assessment limit status
  const fetchAssessmentLimit = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments/limit-status`,
        {},
        activeOrganization?.organization_id
      );
      if (response.ok) {
        const data = await response.json();
        setAssessmentLimit(data);
      }
    } catch (error) {
      console.error('Error fetching assessment limit:', error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenuFor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status === 'archived') {
        params.append('include_archived', 'true');
      }
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments?${params}`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        let items = data.assessments || data.scans || [];
        
        // Backend now handles filtering, no need for client-side filter
        setAssessments(items);
      } else if (response.status === 404) {
        setAssessments([]);
      } else {
        console.error('Error fetching assessments:', response.status);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter assessments based on search and grade
  const filteredAssessments = assessments.filter(assessment => {
    // MagnifyingGlass filter
    if (filters.search) {
      const query = filters.search.toLowerCase();
      const matchesSearch = 
        assessment.company_name?.toLowerCase().includes(query) ||
        assessment.industry?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    
    // Grade filter
    if (filters.grade !== 'all' && assessment.readiness_grade !== filters.grade) {
      return false;
    }
    
    return true;
  });

  const handleArchiveAssessment = async (assessmentId, assessmentName) => {
    if (!window.confirm(`Archive "${assessmentName}"? It will be hidden from your list but can be restored later.`)) {
      return;
    }

    setProcessingId(assessmentId);
    setShowMenuFor(null);

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments/${assessmentId}/archive`,
        { method: 'POST' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        setAssessments(prev => prev.filter(a => a.id !== assessmentId));
      } else {
        alert('Failed to archive assessment');
      }
    } catch (error) {
      console.error('Error archiving assessment:', error);
      alert('Failed to archive assessment');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRestoreAssessment = async (assessmentId, assessmentName) => {
    setProcessingId(assessmentId);
    setShowMenuFor(null);

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments/${assessmentId}/restore`,
        { method: 'POST' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        setAssessments(prev => prev.filter(a => a.id !== assessmentId));
      } else {
        alert('Failed to restore assessment');
      }
    } catch (error) {
      console.error('Error restoring assessment:', error);
      alert('Failed to restore assessment');
    } finally {
      setProcessingId(null);
    }
  };

  const getGradeBadge = (grade) => {
    const colors = {
      A: 'bg-[#14F195]/10 text-[#14F195] border-[#14F195]/20',
      B: 'bg-[#00E5C0]/10 text-[#00E5C0] border-[#00E5C0]/20',
      C: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
      D: 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20',
      F: 'bg-[#718096]/10 text-[#718096] border-[#718096]/20'
    };
    return colors[grade] || colors.F;
  };

  const getGradeGradient = (grade) => {
    const gradients = {
      A: 'bg-[#14F195]',
      B: 'bg-[#00E5C0]',
      C: 'bg-[#F59E0B]',
      D: 'bg-[#EF4444]',
      F: 'bg-[#718096]'
    };
    return gradients[grade] || gradients.F;
  };

  if (orgLoading || !activeOrganization || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={48} className="text-[#00E5C0] animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SEO title="My Assessments" />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Assessments</h1>
            <p className="text-sm text-[#B0B8C4]">
              View and manage all your AI opportunity assessments
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/assessments/history')}
              className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
            >
              <Clock size={20} className="inline mr-2" />
              View History
            </button>
            {/* New Assessment Button - with limit check */}
            {assessmentLimit && !assessmentLimit.can_create_assessment ? (
              <button
                disabled
                className="relative overflow-hidden rounded-full opacity-60 cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-[#718096]"></div>
                <span className="relative z-10 flex items-center gap-2 px-6 py-3 text-white font-bold">
                  <Lock size={20} />
                  Limit Reached
                </span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/assessments/wizard')}
                className="rounded-full bg-[#00E5C0] hover:bg-[#00D4B0] transition-colors"
              >
                <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                  <Plus size={20} />
                  New Assessment
                </span>
              </button>
            )}
          </div>
        </motion.div>

        {/* Plan Banner for Starter */}
        {plan === 'starter' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 p-4 rounded-xl bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.2)]"
          >
            <p className="text-sm text-[#A0AEC0]">
              You can run unlimited assessments but only see your top opportunity per assessment.
              <button
                onClick={() => navigate('/billing')}
                className="ml-2 text-[#00E5C0] hover:text-[#00D4B0] font-semibold transition-colors"
              >
                Upgrade to Pro →
              </button>
            </p>
          </motion.div>
        )}

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* MagnifyingGlass */}
            <div className="flex-1 relative">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search by company or industry..."
                className="w-full pl-12 pr-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#00E5C0] outline-none"
              />
            </div>

            {/* Status Filter Buttons */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setFilters({ ...filters, status: 'active' })}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  filters.status === 'active'
                    ? 'bg-[#00E5C0] text-white shadow-sm'
                    : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setFilters({ ...filters, status: 'archived' })}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                  filters.status === 'archived'
                    ? 'bg-[#00E5C0] text-white shadow-sm'
                    : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
                }`}
              >
                <Archive size={14} />
                Archived
              </button>
            </div>

            {/* Grade Filter */}
            <select
              value={filters.grade}
              onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
              className="px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] cursor-pointer"
              style={{ colorScheme: 'dark' }}
            >
              <option value="all" className="bg-[#1a1a2e] text-white">All Grades</option>
              <option value="A" className="bg-[#1a1a2e] text-white">Grade A</option>
              <option value="B" className="bg-[#1a1a2e] text-white">Grade B</option>
              <option value="C" className="bg-[#1a1a2e] text-white">Grade C</option>
              <option value="D" className="bg-[#1a1a2e] text-white">Grade D</option>
              <option value="F" className="bg-[#1a1a2e] text-white">Grade F</option>
            </select>
          </div>

          {assessments.length > 0 && (
            <div className="mt-4 text-sm text-[#A0AEC0]">
              Showing {filteredAssessments.length} of {assessments.length} assessments
            </div>
          )}
        </motion.div>

        {/* Empty State or Assessments List */}
        {assessments.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-3xl"
          >
            <EmptyState
              icon={Scan}
              title={filters.status === 'archived' ? "No archived assessments" : "No analyses yet"}
              description={filters.status === 'archived' 
                ? "Archived assessments will appear here. Archive assessments you no longer need from the active list."
                : "Run your first AI Opportunity Analysis to uncover automation, efficiency, and intelligence opportunities."
              }
              buttonText={filters.status === 'archived' ? "View Active Assessments" : "Run Opportunity Analysis"}
              onButtonClick={() => filters.status === 'archived' 
                ? setFilters({ ...filters, status: 'active' })
                : navigate('/assessments/wizard')
              }
              gradient=""
            />
          </motion.div>
        ) : filteredAssessments.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-3xl"
          >
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center mx-auto mb-4">
                <Scan size={32} className="text-[#00E5C0]" />
              </div>
              <p className="text-[#A0AEC0] mb-4">No assessments match your filters</p>
              <button
                onClick={() => setFilters({ ...filters, search: '', grade: 'all' })}
                className="rounded-full bg-[#00E5C0] hover:bg-[#00D4B0] transition-colors inline-block"
              >
                <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                  Clear Filters
                </span>
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="space-y-1">
            {filteredAssessments.map((assessment, index) => (
              <motion.div
                key={assessment.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                className="group relative flex items-center gap-4 px-4 py-3 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.03)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] hover:border-[rgba(99,102,241,0.2)] transition-all cursor-pointer"
                onClick={() => navigate(`/assessments/results/${assessment.id}`)}
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-lg ${getGradeGradient(assessment.readiness_grade)} flex items-center justify-center flex-shrink-0`}>
                  <Trophy size={18} className="text-white" />
                </div>
                
                {/* Main Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-white truncate">
                      {assessment.company_name || 'Untitled Assessment'}
                    </h3>
                    
                    {/* Badges */}
                    <div className="flex items-center gap-1.5">
                      {/* Grade Badge */}
                      {assessment.readiness_grade && (
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold border rounded ${getGradeBadge(assessment.readiness_grade)}`}>
                          Grade {assessment.readiness_grade}
                        </span>
                      )}
                      
                      {/* Score Badge */}
                      {assessment.ai_readiness_score && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#00E5C0]/10 text-[#00E5C0] border border-[#00E5C0]/20 rounded">
                          {assessment.ai_readiness_score}/100
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Metadata row */}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {assessment.industry && (
                      <span className="flex items-center gap-1">
                        <Buildings size={11} />
                        {assessment.industry}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {new Date(assessment.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    {assessment.opportunities_count > 0 && (
                      <span className="flex items-center gap-1">
                        <TrendUp size={11} />
                        {assessment.opportunities_count} opportunities
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                  {/* View Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/assessments/results/${assessment.id}`);
                    }}
                    className="px-3 py-1.5 bg-[#00E5C0]/10 text-[#00E5C0] border border-[#00E5C0]/20 rounded-md text-xs font-medium hover:bg-[#00E5C0]/20 transition-colors flex items-center gap-1.5"
                    title="View Results"
                  >
                    <Eye size={12} />
                    View
                  </button>
                  
                  {/* More Actions Menu */}
                  <div className="relative" ref={showMenuFor === assessment.id ? menuRef : null}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (showMenuFor === assessment.id) {
                          setShowMenuFor(null);
                        } else {
                          // Calculate if menu should flip upward
                          const buttonRect = e.currentTarget.getBoundingClientRect();
                          const menuHeight = 150; // Approximate height of the dropdown menu
                          const spaceBelow = window.innerHeight - buttonRect.bottom;
                          setMenuFlipUp(spaceBelow < menuHeight);
                          setShowMenuFor(assessment.id);
                        }
                      }}
                      disabled={processingId === assessment.id}
                      className="p-1.5 hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors disabled:opacity-50"
                      title="More actions"
                    >
                      {processingId === assessment.id ? (
                        <CircleNotch size={14} className="text-gray-400 animate-spin" />
                      ) : (
                        <DotsThreeVertical size={14} className="text-gray-400" />
                      )}
                    </button>
                    
                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {showMenuFor === assessment.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: menuFlipUp ? 5 : -5 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: menuFlipUp ? 5 : -5 }}
                          transition={{ duration: 0.1 }}
                          className={`absolute right-0 z-50 w-48 bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-xl shadow-xl py-1 overflow-hidden ${
                            menuFlipUp ? 'bottom-full mb-2' : 'top-full mt-2'
                          }`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* View Results */}
                          <button
                            onClick={() => {
                              navigate(`/assessments/results/${assessment.id}`);
                              setShowMenuFor(null);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#00E5C0] hover:bg-[rgba(255,255,255,0.05)] transition-all"
                          >
                            <Eye size={14} />
                            View Results
                          </button>
                          
                          <div className="border-t border-[rgba(255,255,255,0.1)] my-1"></div>
                          
                          {/* Archive or Restore based on status */}
                          {filters.status === 'archived' ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestoreAssessment(assessment.id, assessment.company_name || 'Untitled');
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-green-400 hover:bg-green-500/10 transition-all"
                            >
                              <ArrowCounterClockwise size={14} />
                              Restore Assessment
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchiveAssessment(assessment.id, assessment.company_name || 'Untitled');
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.05)] transition-all"
                            >
                              <Archive size={14} />
                              Archive Assessment
                            </button>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                
                {/* Arrow indicator */}
                <ArrowRight size={16} className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MyAssessments;
